import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { api } from "@/lib/api";
import { ParcelCard } from "@/components/wp/ParcelCard";
import { ProductCard } from "@/components/wp/ProductCard";
import { SectionHeading } from "@/components/wp/SectionHeading";
import { Skeleton } from "@/components/ui/skeleton";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let on = true;
    setLoading(true);
    api.get(`/search?q=${encodeURIComponent(q)}`)
      .then((r) => { if (on) setData(r.data); })
      .catch(() => { if (on) setData(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [q]);

  useEffect(() => setInput(q), [q]);

  const submit = (e) => { e.preventDefault(); setParams({ q: input }); };

  const parcels = data?.parcels || [];
  const products = data?.products || [];
  const nothing = !loading && parcels.length === 0 && products.length === 0;

  return (
    <div className="wp-container py-10">
      <SectionHeading eyebrow="Search" title={q ? `Results for “${q}”` : "Find the perfect gift"} subtitle="Search by occasion, budget, recipient, mood or interest." />
      <form onSubmit={submit} className="mt-6 flex max-w-xl gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-wp-ink-muted" />
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Find the perfect gift..." data-testid="search-page-input"
            className="w-full rounded-full border border-border bg-white py-3 pl-10 pr-4 text-sm outline-none focus:ring-2 focus:ring-wp-rose/40" />
        </div>
        <button type="submit" className="rounded-full bg-wp-rose-strong px-5 text-sm font-semibold text-white" data-testid="search-page-submit">Search</button>
      </form>

      {loading ? (
        <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[4/5] w-full rounded-wp-lg" />)}</div>
      ) : nothing ? (
        <p className="mt-12 text-center text-wp-ink-muted">No matches yet — try “gift for sister”, “under ₹799” or “travel”.</p>
      ) : (
        <>
          {parcels.length > 0 && (
            <section className="mt-10">
              <h2 className="font-display text-2xl">Gift Parcels</h2>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">{parcels.map((p, i) => <ParcelCard key={p.id} parcel={p} index={i} />)}</div>
            </section>
          )}
          {products.length > 0 && (
            <section className="mt-12">
              <h2 className="font-display text-2xl">Individual Pieces</h2>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">{products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
